import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchItem, fetchContent } from "@/lib/api";
import { itemPath } from "@/lib/categories";
import { DEVI_FORMS, deviImageFor } from "@/lib/deviForms";
import { useSettings } from "@/context/SettingsContext";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import AudioFeature from "@/components/AudioFeature";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { CalendarDays, Sparkles, ArrowRight, Flame, ScrollText, Music, BookOpen, Waves, ListChecks, Check, ChevronRight, Heart } from "lucide-react";
import { pickScript } from "@/lib/bilingual";

// Shardiya Navratri 2026 — Ghatasthapana Sun 11 Oct 2026 (muhurta 6:19–10:12 AM IST); Vijayadashami Tue 20 Oct 2026.
const GHATASTHAPANA_ISO = "2026-10-11T00:49:00Z"; // 6:19 AM IST
const VIJAYADASHAMI_ISO = "2026-10-20T18:30:00Z"; // end of Tue 20 Oct IST

// Devotional artwork (generated, Tanjore-inspired, on-brand parchment palette)
const DEVI_HERO = "https://static.prod-images.emergentagent.com/jobs/f79bd64e-30d1-4119-8097-970fcb1907b0/images/9eedd23243fffa74dcc1b8a9f0193e124dcea76e5fa14eb8e4940ebc3abe9233.jpeg";
const DEVI_ALTAR = "https://static.prod-images.emergentagent.com/jobs/f79bd64e-30d1-4119-8097-970fcb1907b0/images/b9377d0fc9428cf1f878e05aa1ab70f63d69459f9047f58fddbe091d9d985c16.jpeg";

const SCRIPTURE_SLUGS = ["durga-saptashati", "devi-kavacham", "argala-stotram", "keelaka-stotram", "devi-suktam-rigveda", "lalita-sahasranama"];

const FAQ = [
  { q_en: "What is Ghatasthapana and when is its muhurta in 2026?", q_hi: "घटस्थापना क्या है और 2026 में इसका मुहूर्त कब है?",
    a_en: "Ghatasthapana (kalash sthapana) invokes Shakti at the start of Navratri. In 2026 it falls on Sunday, 11 October, with the muhurta around 6:19 AM to 10:12 AM IST — confirm with your local Panchangam for your city.",
    a_hi: "घटस्थापना (कलश स्थापना) से नवरात्रि के आरम्भ में शक्ति का आवाहन होता है। 2026 में यह रविवार, 11 अक्टूबर को है; मुहूर्त लगभग प्रातः 6:19 से 10:12 बजे तक — अपने नगर हेतु स्थानीय पंचांग से पुष्टि करें।" },
  { q_en: "Can I keep the fast (vrat) and do the puja at home without a purohit?", q_hi: "क्या मैं बिना पुरोहित के व्रत रखकर घर पर पूजा कर सकता/सकती हूँ?",
    a_en: "Yes. A householder is traditionally empowered to observe the Navratri vrat and perform the daily kalash and Durga puja at home — follow the sequence in our detailed Pooja Vidhi with sincerity and clean intent.",
    a_hi: "हाँ। गृहस्थ को श्रद्धापूर्वक नवरात्रि व्रत रखकर घर पर कलश एवं दुर्गा की नित्य पूजा करने का पूर्ण अधिकार है — हमारी विस्तृत पूजा-विधि का अनुसरण करें।" },
  { q_en: "What is Kanya Pujan and when is it done?", q_hi: "कन्या पूजन क्या है और कब किया जाता है?",
    a_en: "On Ashtami or Navami, nine young girls (and often a boy as 'Batuk') are honoured as the living Goddess — their feet are washed and they are offered food (halwa-puri-chana), clothes and dakshina.",
    a_hi: "अष्टमी अथवा नवमी को नौ कन्याओं (एवं प्रायः एक बालक 'बटुक') को साक्षात् देवी-स्वरूप मानकर पूजा जाता है — उनके पाद-प्रक्षालन कर भोजन (हलवा-पूरी-चना), वस्त्र एवं दक्षिणा अर्पित की जाती है।" },
  { q_en: "How is the Durga Saptashati recited during Navratri?", q_hi: "नवरात्रि में दुर्गा सप्तशती का पाठ कैसे करें?",
    a_en: "The paath is preceded by the angas — Devi Kavacham, Argala and Kilaka — and often followed by the Devi Suktam. You may recite the full text daily, or a set number of chapters spread across the nine days.",
    a_hi: "पाठ से पूर्व अंग — देवी कवच, अर्गला एवं कीलक — तथा अन्त में प्रायः देवी सूक्त का पाठ होता है। आप प्रतिदिन सम्पूर्ण पाठ, अथवा नौ दिनों में विभाजित अध्याय पढ़ सकते हैं।" },
];

const useCountdown = (iso) => {
  const target = useMemo(() => new Date(iso).getTime(), [iso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, target - now);
  return { diff, days: Math.floor(diff / 86400000), hours: Math.floor((diff % 86400000) / 3600000), minutes: Math.floor((diff % 3600000) / 60000), seconds: Math.floor((diff % 60000) / 1000) };
};

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 border border-line bg-parchment/70 px-3 py-1 text-xs text-ink-2">
    {Icon && <Icon className="h-3.5 w-3.5 text-saffron" />} {children}
  </span>
);

export default function NavratriHome() {
  const { lang, pick, pickTitle, pickDesc, isDeva } = useLang();
  const settings = useSettings();
  const [puja, setPuja] = useState(null);
  const [stotras, setStotras] = useState([]);
  const [mantras, setMantras] = useState([]);
  const [scriptures, setScriptures] = useState([]);
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dharmartha_navratri26_checklist") || "{}"); } catch { return {}; }
  });
  const [faqOpen, setFaqOpen] = useState(null);
  const cd = useCountdown(GHATASTHAPANA_ISO);

  useEffect(() => {
    fetchItem("navratri-durga-puja-vidhi").catch(() => fetchItem("durga-puja-home").catch(() => null)).then(setPuja).catch(() => {});
    fetchContent({ deity: "Durga", limit: 40 }).then((docs) => {
      setStotras((docs || []).filter((d) => d.type === "stotra" && !SCRIPTURE_SLUGS.includes(d.slug)).slice(0, 6));
      setMantras((docs || []).filter((d) => d.type === "mantra").slice(0, 6));
    }).catch(() => {});
    Promise.all(SCRIPTURE_SLUGS.map((s) => fetchItem(s).catch(() => null)))
      .then((items) => setScriptures(items.filter(Boolean)))
      .catch(() => {});
  }, []);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem("dharmartha_navratri26_checklist", JSON.stringify(next));
  };

  const materials = puja?.materials || [];
  const stepsCount = puja?.steps?.length || 0;
  const mu = puja?.muhurta;
  const scrollToId = (sel) => { const el = document.querySelector(sel); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div data-testid="navratri-page" className="pb-24">
      <Seo
        path="/navratri"
        type="article"
        title="Navratri 2026 — Complete Durga Pooja Vidhi, Muhurta, Mantras & Saptashati"
        description="Shardiya Navratri 2026 (11–20 October) — a complete home guide: Ghatasthapana muhurta, samagri checklist, step-by-step Durga puja vidhi, mantras, Durga Saptashati paath, aarti, kanya pujan and the nine forms of Durga."
        keywords="Navratri 2026, Durga Pooja Vidhi, Ghatasthapana muhurta, Navadurga, Durga Saptashati, kalash sthapana, kanya pujan, Navarna mantra, Navratri mantras, Vijayadashami, Dussehra 2026"
      />

      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute right-[38%] -top-24 hidden h-[560px] w-[560px] text-[var(--gold)] opacity-[0.05] md:block" />
        <div className="relative z-[2] mx-auto grid grid-cols-1 max-w-6xl items-center gap-12 px-5 py-14 md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] md:gap-16 md:px-8 md:py-20 lg:py-24">
          <div className="fade-up">
            <p className="overline-label text-saffron">{pick("शारदीय नवरात्रि · २०२६", "Shardiya Navratri · 2026")}</p>
            <h1 className={`mt-4 text-5xl leading-[1.04] text-ink deva-lh md:text-6xl lg:text-7xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>
              {pick("नवरात्रि पूजा", "Navratri Pooja")}
            </h1>
            {!isDeva && <p className="mt-2 font-deva text-2xl text-saffron deva-lh md:text-3xl">नवरात्रि पूजा २०२६</p>}
            <div className="mt-6 h-px w-20 bg-[var(--gold)]/60" />
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-2">
              {pick(
                "घटस्थापना से विजयादशमी तक — शुभ मुहूर्त, सामग्री, षोडशोपचार, सप्तशती पाठ, मंत्र एवं आरती — दुर्गा पूजा की पूर्ण मार्गदर्शिका।",
                "A complete home guide — from Ghatasthapana to Vijayadashami: muhurta, materials, shodashopachara, Saptashati paath, mantras and aarti, all in one place."
              )}
            </p>

            {cd.diff > 0 ? (
              <div className="mt-9 border-t border-line pt-6" data-testid="navratri-countdown">
                <p className="overline-label text-ink-3">{pick("घटस्थापना तक", "Until Ghatasthapana")}</p>
                <div className="mt-3 flex items-baseline gap-5">
                  {[[cd.days, pick("दिन", "days")], [cd.hours, pick("घण्टे", "hrs")], [cd.minutes, pick("मिनट", "min")], [cd.seconds, pick("सेकण्ड", "sec")]].map(([n, l], i) => (
                    <div key={i} className="text-center">
                      <p className="font-serif-latin text-4xl leading-none tabular-nums text-ink md:text-5xl">{String(n).padStart(2, "0")}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-ink-3">{l}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-sm text-ink-3">{pick("रविवार, ११ अक्टूबर २०२६ · प्रातः ६:१९–१०:१२ (IST)", "Sun, 11 October 2026 · 6:19–10:12 AM IST")}</p>
              </div>
            ) : Date.now() < new Date(VIJAYADASHAMI_ISO).getTime() ? (
              <div className="mt-9 inline-flex items-center gap-3 border border-saffron/40 bg-[rgba(199,80,0,0.05)] px-5 py-3" data-testid="navratri-active">
                <Flame className="h-5 w-5 text-saffron" />
                <div>
                  <p className="font-serif-latin text-lg text-ink">{pick("नवरात्रि चल रही है — जय माता दी!", "Navratri is live — Jai Mata Di!")}</p>
                  <p className="text-xs text-ink-3">{pick("विजयादशमी: मंगलवार, २० अक्टूबर", "Vijayadashami · Tue, 20 Oct")}</p>
                </div>
              </div>
            ) : (
              <p className="mt-9 max-w-xl text-ink-2" data-testid="navratri-post">{pick("इस वर्ष का पर्व सम्पन्न — यह मार्गदर्शिका सम्पूर्ण वर्ष हेतु सुरक्षित रखी जा सकती है।", "This year's celebration has concluded — bookmark this guide for future observances.")}</p>
            )}

            <div className="mt-9 flex flex-wrap items-center gap-3">
              {puja ? (
                <Link to={itemPath(puja)} data-testid="navratri-cta-puja" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  <Flame className="h-4 w-4" /> {pick("पूजा विधि आरम्भ करें", "Begin the Pooja Vidhi")}
                </Link>
              ) : (
                <button type="button" onClick={() => scrollToId("#puja")} data-testid="navratri-cta-puja" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  <Flame className="h-4 w-4" /> {pick("पूजा विधि आरम्भ करें", "Begin the Pooja Vidhi")}
                </button>
              )}
              <button type="button" onClick={() => scrollToId("#scriptures")} className="inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
                <ScrollText className="h-4 w-4" /> {pick("सप्तशती एवं स्तोत्र", "Saptashati & Stotras")}
              </button>
              <ShareButton title="Navratri 2026 · Dharmartha" text={pick("नवरात्रि २०२६ की सम्पूर्ण दुर्गा पूजा मार्गदर्शिका", "The complete Durga Pooja guide for Navratri 2026")} label={pick("साझा करें", "Share")} testid="navratri-share" />
            </div>
          </div>

          {/* Right · framed devotional illustration */}
          <div className="fade-up relative order-first mx-auto w-full max-w-[20rem] md:order-none md:max-w-[26rem]" style={{ animationDelay: "0.12s" }}>
            <div className="pointer-events-none absolute -inset-2 border border-[var(--gold)]/25" aria-hidden="true" />
            <div className="relative overflow-hidden border border-[var(--gold)]/50 bg-[var(--parchment)] shadow-[0_18px_50px_rgba(44,38,33,0.16)]">
              <img src={DEVI_HERO} alt={pick("माँ दुर्गा", "Maa Durga")} className="block h-auto w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ─── MUHURTA STRIP ───────────────────────────────────── */}
      {mu && (
        <section data-testid="navratri-muhurta" className="border-b border-line bg-[rgba(184,134,11,0.05)]">
          <div className="mx-auto grid max-w-6xl gap-6 px-5 py-10 md:grid-cols-2 md:px-8 md:py-14">
            <div className="flex gap-4">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-gold"><CalendarDays className="h-5 w-5" /></div>
              <div>
                <p className="overline-label text-ink-3">{pick("घटस्थापना", "Ghatasthapana")}</p>
                <p className="mt-1 font-serif-latin text-xl text-ink">{mu.sthapana_date}</p>
                <p className="mt-0.5 text-sm text-ink-2">{mu.sthapana_window}</p>
                <p className="mt-3 max-w-md text-[13px] italic text-ink-3">{lang === "hi" ? mu.notes_hi : mu.notes_en}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-saffron text-saffron"><Flame className="h-5 w-5" /></div>
              <div>
                <p className="overline-label text-ink-3">{pick("विजयादशमी", "Vijayadashami")}</p>
                <p className="mt-1 font-serif-latin text-xl text-ink">{mu.visarjan_date}</p>
                <p className="mt-0.5 text-sm text-ink-2">{mu.visarjan_window}</p>
                <p className="mt-3 text-[13px] italic text-ink-3">{pick("अष्टमी/नवमी को कन्या पूजन; विजयादशमी पर पारण।", "Kanya pujan on Ashtami/Navami; paran on Vijayadashami.")}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── GUIDED JOURNEY (3 phases) ───────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="mb-10 flex items-center gap-3">
          <span className="h-px flex-1 bg-line" />
          <span className="overline-label text-gold flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> {pick("मार्गदर्शित यात्रा", "Guided Journey")}</span>
          <span className="h-px flex-1 bg-line" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { i: "01", key: "prepare", en: "Prepare", hi: "तैयारी", desc_en: "Gather your samagri and set up the kalash & altar", desc_hi: "सामग्री एकत्र करें एवं कलश-वेदी सजाएँ", cta_en: "Continue", cta_hi: "आगे बढ़ें", icon: ListChecks, scroll: "#materials" },
            { i: "02", key: "perform", en: "Perform the Pooja", hi: "पूजा सम्पादन", desc_en: `${stepsCount || 16}-step Ghatasthapana & shodashopachara`, desc_hi: `${stepsCount || 16}-चरण घटस्थापना एवं षोडशोपचार`, cta_en: "Open Pooja Vidhi", cta_hi: "पूजा विधि खोलें", icon: Flame, to: puja ? itemPath(puja) : null, scroll: "#puja" },
            { i: "03", key: "chant", en: "Chant & Conclude", hi: "पाठ एवं समापन", desc_en: "Navarna · Saptashati · Arati · Kanya Pujan", desc_hi: "नवार्ण · सप्तशती · आरती · कन्या पूजन", cta_en: "Continue", cta_hi: "आगे बढ़ें", icon: Music, scroll: "#scriptures" },
          ].map((p) => {
            const cls = "group flex w-full flex-col border border-line bg-surface p-7 text-left transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]";
            const inner = (
              <>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--gold)]/50 font-serif-latin text-sm text-gold">{p.i}</span>
                  <span className="h-px flex-1 bg-[var(--line)]" />
                  <p.icon className="h-4 w-4 text-saffron" />
                </div>
                <h3 className={`mt-6 text-2xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick(p.hi, p.en)}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2">{pick(p.desc_hi, p.desc_en)}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick(p.cta_hi, p.cta_en)} <ArrowRight className="h-4 w-4" /></span>
              </>
            );
            return p.to ? (
              <Link key={p.key} to={p.to} data-testid={`navratri-phase-${p.key}`} className={cls}>{inner}</Link>
            ) : (
              <button key={p.key} type="button" onClick={() => scrollToId(p.scroll)} data-testid={`navratri-phase-${p.key}`} className={cls}>{inner}</button>
            );
          })}
        </div>
      </section>

      {/* ─── NAVADURGA — nine forms ──────────────────────────── */}
      <AudioFeature placement="navratri" />

      <section id="navadurga" className="border-y border-line bg-[var(--surface)]">
        <div className="mx-auto max-w-6xl scroll-mt-24 px-5 py-16 md:px-8 md:py-20">
          <div className="mb-8 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("नौ रात्रि, नौ स्वरूप", "Nine nights, nine forms")}</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {DEVI_FORMS.map((n) => (
              <Link key={n.day} to={`/navratri/${n.slug}`} className="group overflow-hidden border border-line bg-parchment/60 transition-all hover:-translate-y-0.5 hover:border-[var(--gold)] hover:shadow-[0_8px_24px_rgba(44,38,33,0.08)]" data-testid={`navadurga-${n.day}`}>
                <div className="relative aspect-[2/3] overflow-hidden bg-[var(--parchment)]">
                  <img src={deviImageFor(n, settings)} alt={n.en} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">{pick("दिन", "Day")} {n.day}</span>
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <p className={`text-2xl text-white drop-shadow ${isDeva ? "font-deva deva-lh" : "font-serif-latin"}`}>{pick(n.deva, n.en)}</p>
                    <p className={`text-sm text-white/90 ${isDeva ? "font-serif-latin" : "font-deva deva-lh"}`}>{pick(n.en, n.deva)}</p>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm leading-relaxed text-ink-3">{pick(n.sig_hi, n.sig_en)}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("विस्तार से", "Learn more")} <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── EXPLORE MORE: Kanya Pujan + Mahavidya ───────────── */}
      <section className="mx-auto max-w-6xl px-5 pt-4 md:px-8" data-testid="navratri-explore-more">
        <div className="grid gap-6 md:grid-cols-2">
          <Link to="/navratri/kanya-pujan" data-testid="navratri-kanya-card" className="group flex items-center gap-5 border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
            <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-saffron text-saffron"><Heart className="h-5 w-5" /></div>
            <div className="flex-1">
              <h3 className={`text-xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("कन्या पूजन विधि", "Kanya Pujan Guide")}</h3>
              <p className="mt-1 text-sm leading-relaxed text-ink-2">{pick("अष्टमी/नवमी को नौ कन्याओं का पूजन — आयु अनुसार देवी-स्वरूप, भोग एवं विधि।", "Honouring nine girls on Ashtami/Navami — age-wise Devi forms, bhog and the full ritual.")}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("देखें", "Open guide")} <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
          <Link to="/mahavidya" data-testid="navratri-mahavidya-card" className="group flex items-center gap-5 border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
            <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-gold"><Sparkles className="h-5 w-5" /></div>
            <div className="flex-1">
              <h3 className={`text-xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("दस महाविद्या", "The Ten Mahavidyas")}</h3>
              <p className="mt-1 text-sm leading-relaxed text-ink-2">{pick("शक्ति के दस महाविद्या स्वरूप — काली से कमला तक, प्रत्येक की महिमा एवं मंत्र।", "The ten wisdom goddesses of Shakti — from Kali to Kamala, each with its significance and mantra.")}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("दर्शन करें", "Explore")} <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── MATERIALS CHECKLIST ─────────────────────────────── */}
      {materials.length > 0 && (
        <section id="materials" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-16 md:px-8 md:py-20">
          <div className="mb-8 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("पूजा सामग्री सूची", "Pooja Materials Checklist")}</h2>
            <span className="overline-label text-ink-3">{materials.length} {pick("वस्तुएँ", "items")}</span>
          </div>
          <p className="max-w-2xl text-ink-3">{pick("तैयारी में मदद के लिए — प्रत्येक वस्तु पर टैप करें। आपकी सूची इस उपकरण में सुरक्षित रहेगी।", "Tap each item as you gather it — your checklist is saved on this device.")}</p>
          <ul className="mt-8 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {materials.map((m, i) => (
              <li key={i}>
                <button data-testid={`navratri-checklist-${i}`} onClick={() => toggle(i)}
                  className={`group flex w-full items-start gap-3 border p-3.5 text-left transition-all ${checked[i] ? "border-[var(--gold)] bg-[rgba(184,134,11,0.08)]" : "border-line bg-parchment/60 hover:border-ink-3"}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${checked[i] ? "border-[var(--gold)] bg-[var(--gold)] text-white" : "border-ink-3"}`}>
                    {checked[i] && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className={`flex-1 text-[15px] leading-snug ${checked[i] ? "text-ink line-through decoration-[var(--gold)]/60" : "text-ink-2"}`}>{pickScript(m, lang)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ─── PUJA VIDHI CTA ──────────────────────────────────── */}
      {puja && (
        <section id="puja" className="border-y border-line bg-[var(--surface)]">
          <div className="mx-auto max-w-6xl scroll-mt-24 px-5 py-16 md:px-8 md:py-20">
            <div className="relative grid grid-cols-1 items-center overflow-hidden border border-line bg-gradient-to-br from-[rgba(199,80,0,0.05)] via-[var(--parchment)] to-[rgba(184,134,11,0.06)] md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
              <div className="relative p-8 md:p-12">
                <p className="overline-label text-saffron">{pick("मुख्य विधि", "The main procedure")}</p>
                <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(puja)}</h2>
                <p className="mt-4 max-w-2xl text-ink-2">{pickDesc(puja)}</p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <Chip icon={ListChecks}>{materials.length} {pick("सामग्री", "materials")}</Chip>
                  <Chip icon={Flame}>{stepsCount} {pick("चरण", "steps")}</Chip>
                  <Chip icon={ScrollText}>{(puja.mantras || []).length} {pick("मंत्र", "mantras")}</Chip>
                  <Chip icon={Sparkles}>{pick("संकल्प + कन्या पूजन", "Sankalpa + Kanya Pujan")}</Chip>
                </div>
                <Link to={itemPath(puja)} data-testid="navratri-open-puja" className="mt-8 inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                  {pick("पूर्ण पूजा विधि खोलें", "Open the full Pooja Vidhi")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="relative overflow-hidden border-t border-line md:border-l md:border-t-0">
                <img src={DEVI_ALTAR} alt={pick("नवरात्रि कलश एवं पूजा वेदी", "Navratri kalash & puja altar")} loading="lazy" className="block h-auto w-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── SCRIPTURES: SAPTASHATI PATH + LALITA ────────────── */}
      {scriptures.length > 0 && (
        <section id="scriptures" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-16 md:px-8 md:py-20">
          <div className="mb-4 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("देवी शास्त्र एवं सप्तशती पाठ", "Devi Scriptures & the Saptashati Path")}</h2>
          </div>
          <p className="max-w-2xl text-ink-3">{pick("अंग-पाठ के क्रम में — कवच, अर्गला, कीलक, फिर सप्तशती एवं देवी सूक्त। सभी मूल देवनागरी एवं IAST लिप्यन्तरण सहित।", "In the order of the paath — Kavacham, Argala, Kilaka, then the Saptashati and Devi Suktam. All in original Devanagari with IAST transliteration.")}</p>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {scriptures.map((item) => (
              <Link key={item.slug} to={itemPath(item)} data-testid={`navratri-scripture-${item.slug}`} className="group flex flex-col border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
                <div className="flex items-center justify-between">
                  <span className="overline-label text-gold">{item.type === "katha" ? pick("ग्रन्थ", "Scripture") : pick("स्तोत्र", "Stotra")}</span>
                  {item.read_time && <span className="text-xs text-ink-3">{item.read_time}</span>}
                </div>
                <h3 className={`mt-4 text-xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(item)}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{pickDesc(item)}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("पाठ करें", "Read & recite")} <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ─── STOTRAS & MANTRAS GRID ──────────────────────────── */}
      {(stotras.length > 0 || mantras.length > 0) && (
        <section id="stotras" className="border-y border-line bg-[var(--surface)]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
            <div className="mb-10 flex items-baseline gap-3">
              <span className="h-px w-10 bg-[var(--gold)]" />
              <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("देवी स्तोत्र एवं मंत्र", "Devi Stotras & Mantras")}</h2>
              <span className="overline-label text-ink-3">{stotras.length + mantras.length} {pick("पाठ", "recitals")}</span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[...mantras, ...stotras].map((item) => (
                <Link key={item.slug} to={itemPath(item)} data-testid={`navratri-chant-${item.slug}`} className="group flex flex-col border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]">
                  <div className="flex items-center justify-between">
                    <span className="overline-label text-gold">{item.type === "mantra" ? pick("मंत्र", "Mantra") : pick("स्तोत्र", "Stotra")}</span>
                    {item.read_time && <span className="text-xs text-ink-3">{item.read_time}</span>}
                  </div>
                  <h3 className={`mt-4 text-xl text-ink deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pickTitle(item)}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{pickDesc(item)}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("पाठ करें", "Read & recite")} <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── AARTI PANEL ─────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:gap-14">
          <div>
            <p className="overline-label text-saffron">{pick("संध्या-आरती", "The Evening Arati")}</p>
            <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("जय अम्बे गौरी", "Jai Ambe Gauri")}</h2>
            <p className="mt-5 max-w-md leading-relaxed text-ink-2">{pick("प्रत्येक दिन प्रातः एवं सायं यह पारम्परिक आरती परिवार सहित गाएँ — घी-दीप अथवा कर्पूर से माँ की परिक्रमा करते हुए।", "Sing this traditional arati morning and evening with the family — circling the Mother with a ghee lamp or camphor flame.")}</p>
            <p className="mt-6 text-sm italic text-ink-3">{pick("आरती के पश्चात् क्षमा-प्रार्थना एवं पुष्पांजलि अवश्य करें।", "Follow the arati with the kshama prayer and pushpanjali.")}</p>
          </div>
          <div className="relative border-l-2 border-[var(--gold)] bg-[var(--surface-alt)]/40 px-6 py-8 md:px-10 md:py-10" data-testid="navratri-aarti-block">
            <div className="space-y-2 font-deva text-lg leading-[2] text-ink">
              {(puja?.aarti ? puja.aarti.split("\n") : [
                "जय अम्बे गौरी, मैया जय श्यामा गौरी।",
                "तुमको निशिदिन ध्यावत, हरि ब्रह्मा शिवरी॥",
              ]).map((line, i) => <p key={i} className={i % 2 === 1 ? "pl-4 text-ink-2" : ""}>{line}</p>)}
            </div>
            <p className="mt-6 border-t border-[var(--line)] pt-4 text-xs text-ink-3">{pick("तत्पश्चात् — सर्वमङ्गलमाङ्गल्ये शिवे सर्वार्थसाधिके। शरण्ये त्र्यम्बके गौरि नारायणि नमोऽस्तु ते॥", "Then — Sarvamangala-mangalye Shive sarvartha-sadhike; Sharanye Tryambake Gauri Narayani namo'stu te.")}</p>
          </div>
        </div>
      </section>

      {/* ─── VIJAYADASHAMI CARD ──────────────────────────────── */}
      {mu && (
        <section className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
          <div className="relative overflow-hidden border border-saffron/30 bg-[rgba(199,80,0,0.04)] p-8 md:p-12">
            <p className="overline-label text-saffron flex items-center gap-2"><Waves className="h-3.5 w-3.5" /> {pick("विजयादशमी एवं पारण", "Vijayadashami & Paran")}</p>
            <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("अधर्म पर धर्म की विजय", "The victory of dharma")}</h2>
            <p className="mt-4 max-w-2xl text-ink-2">{pick("विजयादशमी पर अंतिम आरती कर माँ का आभार व्यक्त करें, जौ (जमवारा) को शिरोधार्य कर बाँटें, एवं विधिपूर्वक व्रत का पारण करें।", "On Vijayadashami perform the final arati with gratitude, distribute the jamvara sprouts with reverence, and break the vrat (paran) properly.")}</p>
            <p className="mt-6 font-deva text-xl text-saffron">या देवी सर्वभूतेषु शक्तिरूपेण संस्थिता · नमस्तस्यै नमस्तस्यै नमस्तस्यै नमो नमः</p>
          </div>
        </section>
      )}

      {/* ─── FAQ ─────────────────────────────────────────────── */}
      <section className="border-t border-line bg-[var(--surface)]">
        <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label text-gold">{pick("प्रश्नोत्तर", "Frequently asked")}</p>
          <h2 className={`mt-3 text-3xl leading-tight text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("आम प्रश्न", "Common questions")}</h2>
          <div className="mt-10 divide-y divide-line border-y border-line">
            {FAQ.map((f, i) => (
              <button key={i} data-testid={`navratri-faq-${i}`} onClick={() => setFaqOpen(faqOpen === i ? null : i)} className="flex w-full items-start justify-between gap-6 py-6 text-left transition-colors hover:bg-parchment/50">
                <div className="flex-1">
                  <p className={`text-lg text-ink ${lang === "en" ? "font-serif-latin" : "font-deva-body"}`}>{pick(f.q_hi, f.q_en)}</p>
                  {faqOpen === i && <p className="mt-3 max-w-3xl leading-relaxed text-ink-2">{pick(f.a_hi, f.a_en)}</p>}
                </div>
                <ChevronRight className={`mt-1 h-5 w-5 shrink-0 text-ink-3 transition-transform ${faqOpen === i ? "rotate-90" : ""}`} />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER CHANT ────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
        <Divider className="mx-auto mb-10 max-w-xs" />
        <p className="font-deva text-3xl text-saffron md:text-4xl">सर्वमङ्गलमाङ्गल्ये शिवे सर्वार्थसाधिके।</p>
        <p className="mt-2 font-deva text-3xl text-saffron md:text-4xl">शरण्ये त्र्यम्बके गौरि नारायणि नमोऽस्तु ते॥</p>
        <p className="mt-6 text-sm italic text-ink-3">{pick("जय माता दी — इस नवरात्रि आपकी साधना मंगलमय हो।", "Jai Mata Di — may your sadhana this Navratri be blessed.")}</p>
      </section>
    </div>
  );
}
